import { Card, Grid, Stack, Typography } from '@mui/material';
import MobileUIDemo from '../demos/MobileUIDemo';
import { useFormContext } from 'react-hook-form';
import { BrandingThemeAddSubmission } from '@/types/brandingThemes';
import { GRID_ROW_STYLES } from '@/constants/common';
import { SVColorPicker, SVAutocomplete } from '@/components/core';
import IntroVideoDemo from '../demos/IntroVideoDemo';
import {
  BRANDING_THEME_CUSTOM_BACKGROUND_OPTIONS,
  BRANDING_THEME_FONT_OPTIONS,
  BRANDING_THEME_INTRO_VIDEO_OPTIONS,
} from '@/constants/brandingThemes';
import FontDemo from '../demos/FontDemo';
import BrandingThemeFilePicker from './BrandingThemeFilePicker';

export default function MobileUIForm() {
  const formMethods = useFormContext<BrandingThemeAddSubmission>();
  const [
    customBottomNavigationSelectedIconColor,
    customHeroBackground,
    customHomeScreenBackground,
    customNavigationBackground,
    primaryColor,
    secondaryColor,
    titleColor,
    titleHeroSectionColor,
    font,
    mobileLogoUrl,
    titleAccentSectionColor,
    introVideo,
  ] = formMethods.watch([
    'mobileUi.customBottomNavigationSelectedIconColor',
    'mobileUi.customHeroBackground',
    'mobileUi.customHomeScreenBackground',
    'mobileUi.customNavigationBackground',
    'mobileUi.primaryColor',
    'mobileUi.secondaryColor',
    'mobileUi.titleColor',
    'mobileUi.titleHeroSectionColor',
    'mobileUi.font',
    'mobileUi.mobileLogoUrl',
    'mobileUi.titleAccentSectionColor',
    'mobileUi.introVideo',
  ]);

  return (
    <Card>
      <Typography variant="h5" mb={2}>
        Mobile UI
      </Typography>
      <Grid sx={GRID_ROW_STYLES} container>
        <Grid size={{ xs: 12, md: 6 }}>
          <SVAutocomplete
            label="Navigation Background"
            name="mobileUi.customNavigationBackground"
            options={BRANDING_THEME_CUSTOM_BACKGROUND_OPTIONS}
            rules={{ required: true }}
          />
          <SVAutocomplete
            label="Hero Background"
            name="mobileUi.customHeroBackground"
            options={BRANDING_THEME_CUSTOM_BACKGROUND_OPTIONS}
            rules={{ required: true }}
          />
          <SVAutocomplete
            label="Home Screen Background"
            name="mobileUi.customHomeScreenBackground"
            options={BRANDING_THEME_CUSTOM_BACKGROUND_OPTIONS}
            rules={{ required: true }}
          />
          <SVColorPicker
            label="Primary Color"
            name="mobileUi.primaryColor"
            rules={{ required: true }}
          />
          <SVColorPicker
            label="Secondary Color"
            name="mobileUi.secondaryColor"
            rules={{ required: true }}
          />
          <SVColorPicker
            label="Bottom Navigation Icon Selected Color"
            name="mobileUi.customBottomNavigationSelectedIconColor"
            rules={{ required: true }}
          />
          <SVColorPicker
            label="Hero Section Color"
            name="mobileUi.titleHeroSectionColor"
            rules={{ required: true }}
          />
          <SVColorPicker
            label="Title Default Color"
            name="mobileUi.titleColor"
            rules={{ required: true }}
          />
          <SVColorPicker
            label="Title Accent Section Color"
            name="mobileUi.titleAccentSectionColor"
            rules={{ required: true }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <MobileUIDemo
            navigationBackground={customNavigationBackground}
            heroBackground={customHeroBackground}
            homeScreenBackground={customHomeScreenBackground}
            primaryColor={primaryColor}
            secondaryColor={secondaryColor}
            bottomNavIconSelectedColor={customBottomNavigationSelectedIconColor}
            titleDefaultColor={titleColor}
            heroSectionColor={titleHeroSectionColor}
            titleAccentSectionColor={titleAccentSectionColor}
            font={font}
            logoImage={mobileLogoUrl}
          />
        </Grid>
      </Grid>
      <Grid spacing={2} sx={GRID_ROW_STYLES} container>
        <Grid size={{ xs: 12, md: 6 }}>
          <Stack spacing={2}>
            <Typography fontWeight="bold" variant="body1">
              Logo for the mobile app
            </Typography>
            <BrandingThemeFilePicker
              name="mobileUi.mobileLogoUrl"
              label="Upload"
              rules={{ required: true }}
              fileName="mobile_logo"
            />
            <Typography color="grey" variant="caption">
              Please upload the following file types: JPG, JPEG, or PNG. The
              file size should be under 1MB and the aspect ratio (width/height)
              should be between 1 and 9.5.
            </Typography>
          </Stack>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          {mobileLogoUrl && (
            <img
              alt="mobile logo"
              style={{ maxWidth: '100%' }}
              src={mobileLogoUrl}
            />
          )}
        </Grid>
      </Grid>
      <Grid spacing={2} sx={GRID_ROW_STYLES} container>
        <Grid size={{ xs: 12, md: 6 }}>
          <SVAutocomplete
            name="mobileUi.introVideo"
            label="Intro Video"
            options={BRANDING_THEME_INTRO_VIDEO_OPTIONS}
            rules={{ required: true }}
          />
        </Grid>
        <IntroVideoDemo introVideo={introVideo} />
      </Grid>
      <Grid spacing={2} container>
        <Grid size={{ xs: 12, md: 6 }}>
          <SVAutocomplete
            name="mobileUi.font"
            label="Font"
            options={BRANDING_THEME_FONT_OPTIONS}
            rules={{ required: true }}
          />
        </Grid>
        <FontDemo font={font} />
      </Grid>
    </Card>
  );
}
